import type { Permission } from '@tekansa/core/permissions';
import {
  Bell,
  Building2,
  ClipboardList,
  Code2,
  Gauge,
  KeyRound,
  Link2,
  MailCheck,
  MessageSquareReply,
  Send,
  ShieldCheck,
  ShieldOff,
  UserRound,
  Users,
  Webhook,
  CalendarClock,
  type LucideIcon,
} from 'lucide-react';

/**
 * Step 17: Settings, one page per setting in five groups. The search matches the label, the
 * one-line description and the keywords. An item shows only when the person may use it.
 */
export interface SettingsItem {
  /** URL below /settings, `<group>/<page>`. */
  path: string;
  label: string;
  description: string;
  keywords: string;
  icon: LucideIcon;
  permission?: Permission;
  /** Any of these permissions. */
  anyOf?: Permission[];
}

export interface SettingsGroup {
  label: string;
  items: SettingsItem[];
}

export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    label: 'Organisation',
    items: [
      {
        path: 'organisation/profile',
        label: 'Profile',
        description: 'Your organisation’s name.',
        keywords: 'company organisation organization name',
        icon: Building2,
        permission: 'settings.view',
      },
      {
        path: 'organisation/users',
        label: 'Users and teams',
        description: 'Who can use Tekansa Connect, their roles, invitations and teams.',
        keywords: 'members people invite invitation team deactivate remove owner admin',
        icon: Users,
        permission: 'users.view',
      },
      {
        path: 'organisation/roles',
        label: 'Roles and permissions',
        description: 'What each role may see and do.',
        keywords: 'role permission access matrix custom scope own team all',
        icon: ShieldCheck,
        permission: 'users.view',
      },
      {
        path: 'organisation/usage',
        label: 'Usage',
        description: 'This month: emails, people, leads, mailboxes, domains, users and checks.',
        keywords: 'usage limits quota plan emails sent verification',
        icon: Gauge,
        permission: 'settings.view',
      },
      {
        path: 'organisation/audit-log',
        label: 'Audit log',
        description: 'Every important change, who made it and when.',
        keywords: 'audit log history security events changes',
        icon: ClipboardList,
        permission: 'users.manage',
      },
    ],
  },
  {
    label: 'My account',
    items: [
      {
        path: 'account/profile',
        label: 'Profile',
        description: 'Your name and email address.',
        keywords: 'name email me my account avatar',
        icon: UserRound,
      },
      {
        path: 'account/security',
        label: 'Password and security',
        description: 'Change your password and sign out of every device.',
        keywords: 'password security sign out log out logout sessions devices',
        icon: KeyRound,
      },
      {
        path: 'account/notifications',
        label: 'Notifications',
        description: 'Which emails you get from this organisation.',
        keywords: 'notifications email alerts digest reconnect reply forwarded bell',
        icon: Bell,
      },
    ],
  },
  {
    label: 'Sending',
    items: [
      {
        path: 'sending/defaults',
        label: 'Defaults',
        description: 'Timezone for new campaigns and the “Pause all sending” switch.',
        keywords: 'timezone time zone pause stop all sending emergency defaults',
        icon: Send,
        permission: 'settings.view',
      },
      {
        path: 'sending/verification',
        label: 'Verification',
        description: 'How emails are checked before sending, and the daily limit.',
        keywords: 'verification verify valid invalid risky check quota limit',
        icon: MailCheck,
        permission: 'settings.view',
      },
      {
        path: 'sending/suppression',
        label: 'Suppression',
        description: 'Blocking invalid emails, and the suppression list.',
        keywords: 'suppression block blocked unsubscribe do not contact invalid list',
        icon: ShieldOff,
        permission: 'leads.view',
      },
      {
        path: 'sending/replies',
        label: 'Replies and CRM',
        description: 'Where replies are forwarded, AI and replying from the Inbox.',
        keywords: 'replies ai inbox reply crm forward email',
        icon: MessageSquareReply,
        permission: 'inbox.manage',
      },
    ],
  },
  {
    label: 'Reports',
    items: [
      {
        path: 'reports/scheduled',
        label: 'Scheduled reports',
        description: 'Reports emailed every week or month.',
        keywords: 'scheduled report email weekly monthly',
        icon: CalendarClock,
        permission: 'analytics.export',
      },
      {
        path: 'reports/share-links',
        label: 'Share links',
        description: 'Report pages shared with clients.',
        keywords: 'share link client public password revoke',
        icon: Link2,
        permission: 'analytics.export',
      },
    ],
  },
  {
    label: 'Developers',
    items: [
      {
        path: 'developers/api-keys',
        label: 'API keys',
        description: 'Keys for your own tools and integrations.',
        keywords: 'api key token integration developer',
        icon: Code2,
        permission: 'integrations.view',
      },
      {
        path: 'developers/webhooks',
        label: 'Webhooks',
        description: 'Send replies to your CRM as they happen.',
        keywords: 'webhook crm hubspot zapier integration events',
        icon: Webhook,
        permission: 'integrations.edit',
      },
    ],
  },
];

/** Old Settings URLs (before Step 17) → the new pages. */
export const OLD_SETTINGS_PATHS: Record<string, string> = {
  profile: 'account/profile',
  team: 'organisation/users',
  teams: 'organisation/users',
  roles: 'organisation/roles',
  analytics: 'organisation/profile',
  'organisation/goals': 'organisation/profile',
  'audit-log': 'organisation/audit-log',
  workspaces: 'sending/defaults',
  verification: 'sending/verification',
  replies: 'sending/replies',
  'api-keys': 'developers/api-keys',
  deliverability: 'sending/defaults',
};

/** Items whose label, description or keywords contain every word of the search. */
export function matchSettings(items: SettingsItem[], search: string): SettingsItem[] {
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return items;
  return items.filter((i) => {
    const text = `${i.label} ${i.description} ${i.keywords}`.toLowerCase();
    return words.every((w) => text.includes(w));
  });
}
